import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const navLinks = [
  { label: 'Úvod', href: '#uvod' },
  { label: 'Služby', href: '#sluzby' },
  { label: 'Referencie', href: '#referencie' },
  { label: 'Cenník', href: '#cennik' },
  { label: 'O Boostio', href: '/o-nas' },
  { label: 'Blog', href: '/blog' },
  { label: 'Kontakt', href: '#kontakt' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Lock body scroll when menu is open */
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
      /* tiny delay so CSS transition fires */
      requestAnimationFrame(() => requestAnimationFrame(() => setMenuVisible(true)));
    } else {
      setMenuVisible(false);
      const t = setTimeout(() => { document.body.style.overflow = ''; }, 320);
      return () => clearTimeout(t);
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleNav = (href: string) => {
    setMobileOpen(false);
    if (href.startsWith('/')) {
      navigate(href);
    } else {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-zinc-950/95 backdrop-blur-md border-b border-zinc-800/60 py-3' : 'bg-transparent py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <a
            href="#uvod"
            onClick={(e) => { e.preventDefault(); handleNav('#uvod'); }}
            className="flex items-center gap-2 cursor-pointer"
          >
            <div id="navbar-logo-box" className="logo-icon-wrap w-8 h-8 flex items-center justify-center bg-orange-500 rounded-lg overflow-hidden">
              <i id="navbar-rocket-icon" className="logo-rocket ri-rocket-2-fill text-white text-sm" />
            </div>
            <span className="text-white font-bold text-xl tracking-tight font-sans">
              boost<span className="text-orange-500">io</span>
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNav(link.href); }}
                className="text-zinc-400 hover:text-white text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="#kontakt"
              onClick={(e) => { e.preventDefault(); handleNav('#kontakt'); }}
              className="bg-orange-500 hover:bg-orange-400 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Konzultácia zadarmo
            </a>
          </div>

          {/* Hamburger button */}
          <button
            className="md:hidden relative w-9 h-9 flex items-center justify-center cursor-pointer z-[60]"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
            aria-expanded={mobileOpen}
          >
            {/* Three bars → X morphing */}
            <span className="flex flex-col gap-[5px] w-5">
              <span
                className="block h-[2px] bg-white rounded-full origin-center transition-all duration-300"
                style={{
                  transform: mobileOpen ? 'translateY(7px) rotate(45deg)' : 'none',
                }}
              />
              <span
                className="block h-[2px] bg-white rounded-full transition-all duration-300"
                style={{
                  opacity: mobileOpen ? 0 : 1,
                  transform: mobileOpen ? 'scaleX(0)' : 'scaleX(1)',
                }}
              />
              <span
                className="block h-[2px] bg-white rounded-full origin-center transition-all duration-300"
                style={{
                  transform: mobileOpen ? 'translateY(-7px) rotate(-45deg)' : 'none',
                }}
              />
            </span>
          </button>
        </div>
      </header>

      {/* Backdrop overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          style={{
            background: 'rgba(0,0,0,0.65)',
            backdropFilter: 'blur(3px)',
            opacity: menuVisible ? 1 : 0,
            transition: 'opacity 0.3s ease',
          }}
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="fixed top-0 right-0 bottom-0 w-[78vw] max-w-[320px] z-50 md:hidden flex flex-col"
          style={{
            background: 'linear-gradient(160deg, #111113 0%, #0d0d0f 100%)',
            borderLeft: '1px solid rgba(249,115,22,0.12)',
            transform: menuVisible ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 0.32s cubic-bezier(0.4,0,0.2,1)',
            boxShadow: menuVisible ? '-20px 0 60px rgba(0,0,0,0.6)' : 'none',
          }}
        >
          {/* Drawer header */}
          <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-zinc-800/60">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center bg-orange-500 rounded-md">
                <i className="ri-rocket-2-fill text-white text-xs" />
              </div>
              <span className="text-white font-bold text-lg tracking-tight">
                boost<span className="text-orange-500">io</span>
              </span>
            </div>
            <button
              onClick={() => setMobileOpen(false)}
              className="w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          {/* Nav links */}
          <nav className="flex-1 px-4 py-5 flex flex-col gap-1 overflow-y-auto">
            {navLinks.map((link, i) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNav(link.href); }}
                className="flex items-center gap-3 px-3 py-3.5 rounded-xl text-zinc-300 hover:text-white hover:bg-white/[0.05] text-base font-medium transition-all duration-200 cursor-pointer"
                style={{
                  opacity: menuVisible ? 1 : 0,
                  transform: menuVisible ? 'translateX(0)' : 'translateX(20px)',
                  transition: `opacity 0.3s ease ${0.06 + i * 0.04}s, transform 0.3s ease ${0.06 + i * 0.04}s, color 0.2s, background 0.2s`,
                }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500/50 flex-shrink-0" />
                {link.label}
              </a>
            ))}
          </nav>

          {/* CTA at bottom */}
          <div
            className="px-4 pb-8 pt-4 border-t border-zinc-800/60"
            style={{
              opacity: menuVisible ? 1 : 0,
              transform: menuVisible ? 'translateY(0)' : 'translateY(12px)',
              transition: 'opacity 0.35s ease 0.32s, transform 0.35s ease 0.32s',
            }}
          >
            <a
              href="#kontakt"
              onClick={(e) => { e.preventDefault(); handleNav('#kontakt'); }}
              className="block w-full text-white text-sm font-bold py-3.5 rounded-xl text-center cursor-pointer whitespace-nowrap transition-all duration-200 active:scale-95"
              style={{
                background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                boxShadow: '0 0 20px rgba(249,115,22,0.3)',
              }}
            >
              Konzultácia zadarmo
            </a>
          </div>
        </div>
      )}
    </>
  );
}
